import * as React from "react";
import { IFilterControl, IFilterItem } from "../../FilterSetupPlaceholder";
import { OperatorSelector, IOperator } from "../OperatorSelector";
import { AtollonTextField } from "../../../../../../ui/TextField/AtollonTextField";
import { useFilterGuard } from "../../../../../../../hooks/useFilterGuard";
import styles from "./TextFilterControl.module.css";
import { Flex, Text } from "@adobe/react-spectrum";

export interface ITextFilterControlProps {
    filter: IFilterControl;
    onFilterChange: (filter: IFilterControl) => void;
}

const TEXT_OPERATORS: IOperator[] = [
    { key: "contains", label: "Contains" },
    { key: "does_not_contain", label: "Does Not Contain" },
    { key: "is_empty", label: "Is Empty" },
    { key: "is_not_empty", label: "Is Not Empty" },
];

export const TextFilterControl = React.forwardRef<
    HTMLDivElement,
    ITextFilterControlProps
>((props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
    // Track if we're updating operator internally to prevent useEffect from resetting it
    const isInternalOperatorUpdateRef = React.useRef<boolean>(false);
    const lastSyncedFilterInfoRef = React.useRef<string>(
        JSON.stringify(filter.filterInfo)
    );
    const currentFilterItem = React.useMemo(
        () =>
            filter.filterInfo[0] || {
                name: "value",
                type: "contains",
                value: "",
            },
        [filter.filterInfo]
    );

    const [operator, setOperator] = React.useState<string>(() => {
        const initialType = currentFilterItem.type || "contains";
        // Migrate removed operators to "contains"
        if (initialType === "equals" || initialType === "begins_with" || initialType === "ends_with") {
            return "contains";
        }
        return initialType;
    });
    const [value, setValue] = React.useState<string>(
        currentFilterItem.value || ""
    );

    React.useEffect(() => {
        syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
        // Skip if this is an internal update we just triggered
        if (isInternalOperatorUpdateRef.current) {
            isInternalOperatorUpdateRef.current = false;
            return;
        }

        const currentItem = filter.filterInfo[0];
        if (currentItem) {
            // Default to "contains" if type is missing, empty, or is a removed operator
            let newOperator = currentItem.type || "contains";
            // Migrate removed operators to "contains"
            const isRemovedOperator = newOperator === "equals" || newOperator === "begins_with" || newOperator === "ends_with";
            if (isRemovedOperator) {
                newOperator = "contains";
            }
            const currentFilterInfoStr = JSON.stringify(filter.filterInfo);
            
            // Only update if filter.filterInfo actually changed from parent
            if (currentFilterInfoStr !== lastSyncedFilterInfoRef.current) {
                setOperator(newOperator);
                setValue(currentItem.value || "");
                lastSyncedFilterInfoRef.current = currentFilterInfoStr;
                
                // If we migrated a removed operator, update the filterInfo to persist the change
                if (isRemovedOperator) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentItem,
                            type: "contains",
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        isInternalOperatorUpdateRef.current = true;
                    }
                }
            }
        }
    }, [filter.filterInfo, operator, notifyChange]);

    const handleOperatorChange = React.useCallback(
        (newOperator: string) => {
            setOperator(newOperator);
            // For is_empty and is_not_empty operators, set value to empty string
            const filterValue =
                newOperator === "is_empty" || newOperator === "is_not_empty"
                    ? ""
                    : value;
            const updatedFilterInfo: IFilterItem[] = [
                {
                    ...currentFilterItem,
                    type: newOperator,
                    value: filterValue,
                },
            ];
            const didNotify = notifyChange({
                ...filter,
                filterInfo: updatedFilterInfo,
            });
            if (didNotify) {
                // Mark this as an internal update to prevent useEffect from resetting
                isInternalOperatorUpdateRef.current = true;
            }
        },
        [filter, currentFilterItem, value, notifyChange, operator]
    );

    const handleValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setValue(newValue);

            // Clear any existing debounce timeout
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            // Debounce filter change by 500ms
            debounceRef.current = setTimeout(() => {
                const updatedFilterInfo: IFilterItem[] = [
                    {
                        ...currentFilterItem,
                        type: operator,
                        value: newValue,
                    },
                ];
                notifyChange({
                    ...filter,
                    filterInfo: updatedFilterInfo,
                });
            }, 500);
        },
        [filter, currentFilterItem, operator, notifyChange]
    );

    // Cleanup debounce on unmount
    React.useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div ref={forwardedRef} className={styles.textFilterControl}>
            <Flex direction="column" gap="size-200">
                {/* Operator row: Label + Selector */}
                <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="size-200"
                    UNSAFE_className={styles.operatorRow}
                >
                    <Text UNSAFE_className={styles.operatorLabel}>
                        {filter.label || filter.name}
                    </Text>
                    <div className={styles.operatorSelectorWrapper}>
                        <OperatorSelector
                            operators={TEXT_OPERATORS}
                            selectedOperator={operator}
                            onOperatorChange={handleOperatorChange}
                            aria-label="Text filter operator"
                        />
                    </div>
                </Flex>

                {/* Value input row */}
                {operator !== "is_empty" && operator !== "is_not_empty" && (
                    <div className={styles.valueInput}>
                        <AtollonTextField
                            value={value}
                            onChange={handleValueChange}
                            aria-label={`${
                                filter.label || filter.name
                            } filter value`}
                            width="100%"
                        />
                    </div>
                )}
            </Flex>
        </div>
    );
});

TextFilterControl.displayName = "TextFilterControl";
